import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a duration in milliseconds to a human-readable French string.
 * Examples: "5min", "2h30", "1j 3h"
 */
export function formatDuration(ms) {
  if (ms == null || isNaN(ms)) return null;
  const absMs = Math.abs(ms);
  const minutes = Math.floor(absMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  const remainingMinutes = minutes % 60;

  if (days > 0) {
    return remainingHours > 0 ? `${days}j ${remainingHours}h` : `${days}j`;
  }
  if (hours > 0) {
    return remainingMinutes > 0 ? `${hours}h${String(remainingMinutes).padStart(2, '0')}` : `${hours}h`;
  }
  return `${minutes}min`;
}

/**
 * Compute ETA text and color for a shipment based on its status and dates.
 * Returns { text: string, color: string } or null if not applicable.
 */
export function getShipmentETA(shipment) {
  if (!shipment) return null;
  const now = Date.now();

  if ((shipment.status === 'scheduled' || shipment.status === 'planned') && shipment.departure_date) {
    const departure = new Date(shipment.departure_date).getTime();
    const diff = departure - now;
    if (diff > 0) {
      return { text: `Depart dans ${formatDuration(diff)}`, color: 'blue' };
    }
    return { text: 'Depart imminent', color: 'orange' };
  }

  if (shipment.status === 'in_transit' && shipment.expected_arrival_date) {
    const arrival = new Date(shipment.expected_arrival_date).getTime();
    const diff = arrival - now;
    if (diff > 0) {
      return { text: `Arrivee dans ${formatDuration(diff)}`, color: 'blue' };
    }
    return { text: `Arrivee depassee de ${formatDuration(Math.abs(diff))}`, color: 'orange' };
  }

  if (shipment.status === 'delayed' && shipment.expected_arrival_date) {
    const arrival = new Date(shipment.expected_arrival_date).getTime();
    const diff = now - arrival;
    if (diff > 0) {
      return { text: `En retard de ${formatDuration(diff)}`, color: 'red' };
    }
    return { text: 'Retard signale', color: 'red' };
  }

  return null;
}

/**
 * Returns a human-readable French relative time string from an ISO date.
 * Examples: "à l'instant", "il y a 5h", "il y a 3j"
 * Also returns isStale: true when >= thresholdDays (default 3).
 */
export function daysSinceUpdate(updatedAt, thresholdDays = 3) {
  if (!updatedAt) return { text: '-', isStale: false };
  const now = Date.now();
  const updated = new Date(updatedAt).getTime();
  const diffMs = now - updated;
  if (diffMs < 0) return { text: 'à l\'instant', isStale: false };

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  let text;
  if (minutes < 1) text = 'à l\'instant';
  else if (minutes < 60) text = `il y a ${minutes}min`;
  else if (hours < 24) text = `il y a ${hours}h`;
  else text = `il y a ${days}j`;

  return { text, isStale: days >= thresholdDays };
}
